import React, { useState, useEffect } from 'react';
import { STOCK_HISTORY_TYPES as TYPE_UA, formatStockHistoryDate, fmtPrice, renderCommentWithSmartLinks, StockMovementDetails } from '../stockHistoryUtils';
import { adminProductApi } from '../../../../api/adminProductApi';

const StockHistoryModal = ({ show, productId, productName, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editComment, setEditComment] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [commentSaveError, setCommentSaveError] = useState(null);

  useEffect(() => {
    setEditingId(null);
    setEditComment('');
    setSavingId(null);
    setCommentSaveError(null);
  }, [show, productId]);

  useEffect(() => {
    if (!show || productId == null || productId === '') {
      setRows([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    adminProductApi
      .getStockHistory(productId)
      .then(res => {
        const data = res.data && res.data.data;
        setRows(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.data ||
          err.message ||
          'Не вдалося завантажити історію';
        setError(String(msg));
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [show, productId]);

  const startEditComment = (row) => {
    setCommentSaveError(null);
    setEditingId(row.id);
    setEditComment(row.comment == null ? '' : String(row.comment));
  };

  const cancelEditComment = () => {
    setEditingId(null);
    setEditComment('');
    setCommentSaveError(null);
  };

  const saveComment = async (row) => {
    if (productId == null || productId === '' || row.id == null) return;
    setSavingId(row.id);
    setCommentSaveError(null);
    const trimmed = editComment.trim();
    const payloadComment = trimmed === '' ? '' : editComment;
    try {
      await adminProductApi.patchStockHistoryComment(productId, row.id, payloadComment);
      const stored = trimmed === '' ? null : trimmed;
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, comment: stored } : r)));
      setEditingId(null);
      setEditComment('');
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.data ||
        err.message ||
        'Не вдалося зберегти коментар';
      setCommentSaveError(String(msg));
    } finally {
      setSavingId(null);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop show" style={{ zIndex: 1040 }} />
      <div
        className="modal show"
        tabIndex="-1"
        role="dialog"
        style={{ display: 'block', zIndex: 1050 }}
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
          role="document"
          style={{ maxWidth: 'min(1400px, 98vw)', width: '98vw', margin: '1.75rem auto' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header align-items-start">
              <div className="pr-4">
                <h5 className="modal-title font-weight-bold mb-0">Історія змін залишків</h5>
                {(productName || (productId != null && productId !== '')) && (
                  <div className="text-muted small mt-1">
                    {productName || ''}
                    {productName && productId != null && productId !== '' ? ' ' : ''}
                    {productId != null && productId !== '' ? `(ID ${productId})` : ''}
                  </div>
                )}
              </div>
              <button type="button" className="close" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body text-left">
              {loading && <p className="text-muted mb-0">Завантаження…</p>}
              {error && <div className="alert alert-danger">{error}</div>}
              {commentSaveError && !error && <div className="alert alert-warning py-2">{commentSaveError}</div>}
              {!loading && !error && rows.length === 0 && (
                <p className="text-muted mb-0">Записів ще немає.</p>
              )}
              {!loading && rows.length > 0 && (
                <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                  <table className="table table-sm table-striped mb-0" style={{ fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Тип</th>
                        <th className="text-right">На складі до</th>
                        <th className="text-right">Зміна</th>
                        <th className="text-right">На складі після</th>
                        <th className="text-right">Закуп до</th>
                        <th className="text-right">Опер. закуп</th>
                        <th className="text-right">Закуп після</th>
                        <th>Користувач</th>
                        <th style={{ minWidth: '14rem' }}>Коментар</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => (
                        <tr key={row.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {row.createdAtDisplay || formatStockHistoryDate(row.createdAt)}
                          </td>
                          <td>{TYPE_UA[row.type] || row.type}<StockMovementDetails row={row} /></td>
                          <td className="text-right">{row.qtyBefore}</td>
                          <td className="text-right">{row.qtyChange > 0 ? `+${row.qtyChange}` : row.qtyChange}</td>
                          <td className="text-right">{row.qtyAfter}</td>
                          <td className="text-right">{fmtPrice(row.purchasePriceBefore)}</td>
                          <td className="text-right">{fmtPrice(row.operationPurchasePrice)}</td>
                          <td className="text-right">{fmtPrice(row.purchasePriceAfter)}</td>
                          <td className="text-right">{row.userId != null ? row.userId : '—'}</td>
                          <td style={{ maxWidth: '280px', verticalAlign: 'top' }}>
                            {editingId === row.id ? (
                              <div className="d-flex flex-column" style={{ gap: '0.35rem' }}>
                                <textarea
                                  className="form-control form-control-sm"
                                  rows={2}
                                  value={editComment}
                                  onChange={(e) => setEditComment(e.target.value)}
                                  disabled={savingId === row.id}
                                  placeholder="Коментар (порожньо — очистити)"
                                />
                                <div className="d-flex flex-wrap" style={{ gap: '0.25rem' }}>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={savingId === row.id}
                                    onClick={() => saveComment(row)}
                                  >
                                    {savingId === row.id ? 'Збереження…' : 'Зберегти'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    disabled={savingId === row.id}
                                    onClick={cancelEditComment}
                                  >
                                    Скасувати
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span style={{ wordBreak: 'break-word' }}>
                                {renderCommentWithSmartLinks(row.comment, row.type)}
                                {'\u00A0'}
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-secondary border-0"
                                  style={{
                                    lineHeight: 1,
                                    verticalAlign: 'baseline',
                                    display: 'inline-block',
                                    fontSize: 'inherit',
                                    padding: 0
                                  }}
                                  onClick={() => startEditComment(row)}
                                  title="Редагувати коментар"
                                  aria-label="Редагувати коментар"
                                >
                                  <i className="bi bi-pencil" aria-hidden="true" style={{ fontSize: '1em' }} />
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Закрити
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StockHistoryModal;
